import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export const CatCard = ({ cat }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [selectedImage, setSelectedImage] = useState(null);

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const years = today.getFullYear() - birth.getFullYear();
    const months = today.getMonth() - birth.getMonth();
    
    let ageYears = years;
    let ageMonths = months;
    
    if (months < 0) {
      ageYears--;
      ageMonths = 12 + months;
    }
    
    if (ageYears > 0) {
      return language === 'pt' 
        ? `${ageYears} ${ageYears === 1 ? 'ano' : 'anos'}${ageMonths > 0 ? ` e ${ageMonths} ${ageMonths === 1 ? 'mês' : 'meses'}` : ''}`
        : `${ageYears} ${ageYears === 1 ? 'year' : 'years'}${ageMonths > 0 ? ` and ${ageMonths} ${ageMonths === 1 ? 'month' : 'months'}` : ''}`;
    }
    return language === 'pt' 
      ? `${ageMonths} ${ageMonths === 1 ? 'mês' : 'meses'}`
      : `${ageMonths} ${ageMonths === 1 ? 'month' : 'months'}`;
  };

  const formatDate = (dateString) => {
    // Parse the date string directly (YYYY-MM-DD format)
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <>
      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300">
        {/* Main Image - Original proportions */}
        <div className="relative w-full overflow-hidden group">
          <img
            loading="lazy" decoding="async" src={cat.mainImage}
            alt={cat.name}
            className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        <CardHeader>
          <CardTitle className="text-2xl text-brown-900">{cat.name}</CardTitle>
          {cat.registeredName && (
            <p className="text-sm text-gray-600 mt-1">{cat.registeredName}</p>
          )}
          {cat.gender && (
            <p className="text-sm text-gray-600">{cat.gender}</p>
          )}
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Birth Date */}
          <div>
            <p className="text-sm font-medium text-gray-500">{t.ourCats.birthDate}</p>
            <p className="text-base text-brown-900">
              {formatDate(cat.birthDate)}
            </p>
          </div>

          {/* Origin */}
          {cat.origin && (
            <div>
              <p className="text-sm font-medium text-gray-500">Origem</p>
              <p className="text-base text-brown-900">{cat.origin}</p>
            </div>
          )}

          {/* Family Origin */}
          {cat.familyOrigin && (
            <div>
              <p className="text-sm font-medium text-gray-500">Origem Familiar</p>
              <p className="text-sm text-gray-700">{cat.familyOrigin}</p>
            </div>
          )}

          {/* Color */}
          <div>
            <p className="text-sm font-medium text-gray-500">{t.ourCats.color}</p>
            <p className="text-base text-brown-900">{cat.color}</p>
          </div>

          {/* Titles */}
          {cat.titles && (
            <div>
              <p className="text-sm font-medium text-gray-500">{t.ourCats.titles}</p>
              <div className="text-base text-brown-900 whitespace-pre-line">
                {cat.titles}
              </div>
            </div>
          )}

          {/* Additional Info */}
          {cat.additionalInfo && (
            <div>
              <p className="text-sm font-medium text-gray-500">{t.ourCats.additionalInfo}</p>
              <p className="text-base text-brown-900 leading-relaxed">{cat.additionalInfo}</p>
            </div>
          )}

          {/* Pedigree Link */}
          {cat.pedigreeLink && (
            <div className="pt-4">
              <a
                href={cat.pedigreeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-brown-900 text-white rounded-full hover:bg-brown-800 transition-colors font-medium text-sm"
              >
                Árvore Genealógica Internacional
              </a>
            </div>
          )}

          {/* Gallery Thumbnails */}
          {cat.gallery && cat.gallery.length > 1 && (
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">{t.ourCats.viewMore}</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {cat.gallery.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden hover:ring-2 hover:ring-brown-900 transition-all"
                  >
                    <img
                      loading="lazy" decoding="async" src={img}
                      alt={`${cat.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Modal */}
      <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-2xl md:max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{cat.name}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="relative w-full flex items-center justify-center">
              <img
                loading="lazy" decoding="async" src={selectedImage}
                alt={cat.name}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
